import Choices from 'choices.js';
import axios from 'axios';

import Module from '@/scripts/old/classes/Module';
import { parseAttr, isDefined } from '@/scripts/old/helpers/utils';

const NAMESPACE = 'data-choices';
const EMPTY_AJAX_CACHE_KEY = '__default';
const I18N = {
  loadingText: 'Загрузка...',
  loadingError: 'Ошибка',
  noResultsText: 'Ничего не найдено',
  noChoicesText: 'Ничего не найдено',
  itemSelectText: '',
  addItemText: (value) => `Press Enter to add <b>"${value}"</b>`,
  maxItemText: (maxItemCount) => `Only ${maxItemCount} values can be added.`,
};

class ChoicesModule extends Module {
  constructor() {
    super(`[${NAMESPACE}]`);
  }

  // TODO: Reactive bind with select element (disabled, invalid, etc.)
  // TODO: RTL
  // TODO: restore select element after destroy

  initInstance(el) {
    const config = parseAttr(el, NAMESPACE);
    const isDisabled = el.getAttribute('disabled') !== null;
    const ajaxCache = {};
    const ajax = config.ajax ? ({
 url: '',
      initial: true,
      search: true,
      cache: false,
      queryParam: 'search',
      delay: 200,
...config.ajax,
}) : false;

    let instance;
    let ajaxTimer;

    // Fix placeholder for select-one
    // TODO: remove when library get fix
    // https://github.com/jshjohnson/Choices/issues/304
    // https://github.com/jshjohnson/Choices/issues/301
    // https://github.com/jshjohnson/Choices/pull/294
    // https://github.com/jshjohnson/Choices/pull/269
    // https://github.com/jshjohnson/Choices/issues/217
    let selectOnePlaceholder = config.placeholderValue;
    let selectOneInitialValue = '';
    const isSelectOne = el.type === 'select-one';

    if (isSelectOne) {
      const options = el.querySelectorAll('option');
      if (options.length) {
        Array.prototype.forEach.call(options, (option) => {
          if (option.getAttribute('placeholder') !== null) {
            selectOnePlaceholder = option.innerText;
            option.parentNode.removeChild(option);
            return;
          }

          if (option.getAttribute('selected') !== null) {
            selectOneInitialValue = option.getAttribute('value');
          }
        });
      }
    }

    const setNotice = (text) => {
      const notice = instance._getTemplate('notice', text);
      instance.choiceList.innerHTML = '';
      instance.choiceList.appendChild(notice);
    };

    const setLoading = () => {
      setNotice(I18N.loadingText);
    };

    const setLoadingError = () => {
      setNotice(I18N.loadingError);
    };

    const fixSelectOnePlaceholder = () => {
      if (instance.isSelectOneElement && selectOnePlaceholder) {
        instance.setChoices([{ label: selectOnePlaceholder, value: '', placeholder: true }], 'value', 'label', false);
        if (instance.currentState.choices.length === 1) {
          setNotice(I18N.noResultsText);
        }
      }
    };

    const fetchChoices = (query) => {
      if (ajax.cache) {
        const cachedChoices = ajaxCache[query || EMPTY_AJAX_CACHE_KEY];
        if (cachedChoices) {
          instance.setChoices(cachedChoices, 'value', 'label', true);
          return;
        }
      }

      setLoading(instance);
      axios.get(
        ajax.url,
        {
          params: {
            [ajax.queryParam]: query,
          },
        },
)
        .then(({ data }) => {
          if (ajax.cache) {
            ajaxCache[query || EMPTY_AJAX_CACHE_KEY] = data;
          }

          instance.setChoices(data, 'value', 'label', true);
          fixSelectOnePlaceholder();
        })
        .catch(() => {
          setLoadingError();
          fixSelectOnePlaceholder();
        });
    };

    // Init choices instance
    instance = new Choices(el, {
      items: config.items || [],
      choices: config.choices || [],
      renderChoiceLimit: config.renderChoiceLimit || -1,
      maxItemCount: config.maxItemCount || -1,
      addItems: true,
      removeItems: isDefined(config.removeItems) ? config.removeItems : true,
      removeItemButton: isDefined(config.removeItems) ? config.removeItems : true,
      editItems: isDefined(config.editItems) ? config.editItems : true,
      duplicateItems: false,
      delimiter: ',',
      paste: true,
      searchEnabled: isDefined(config.searchEnabled) ? config.searchEnabled : true,
      searchChoices: !ajax || !ajax.search,
      searchFloor: 1,
      searchResultLimit: 10,
      searchFields: ['label', 'value'],
      position: 'auto',
      resetScrollPosition: true,
      regexFilter: null,
      shouldSort: false,
      shouldSortItems: false,
      placeholder: true,
      placeholderValue: config.placeholderValue,
      searchPlaceholderValue: config.searchPlaceholderValue,
      renderSelectedChoices: 'auto',
      loadingText: I18N.loadingText,
      noResultsText: I18N.noResultsText,
      noChoicesText: I18N.noChoicesText,
      itemSelectText: I18N.itemSelectText,
      addItemText: I18N.addItemText,
      maxItemText: I18N.maxItemText,
      itemComparer: (choice, item) => choice === item,
      classNames: {
        containerOuter: 'choices',
        containerInner: 'choices__inner',
        input: 'choices__input',
        inputCloned: 'choices__input_cloned',
        list: 'choices__list',
        listItems: 'choices__list_multiple',
        listSingle: 'choices__list_single',
        listDropdown: 'choices__list_dropdown',
        item: 'choices__item',
        itemSelectable: 'choices__item_selectable',
        itemDisabled: 'choices__item_disabled',
        itemChoice: 'choices__item_choice',
        placeholder: 'choices__placeholder',
        group: 'choices__group',
        groupHeading: 'choices__heading',
        button: 'choices__button',
        activeState: '_active',
        focusState: '_focused',
        openState: '_open',
        disabledState: '_disabled',
        highlightedState: '_highlighted',
        hiddenState: '_hidden',
        flippedState: '_flipped',
        loadingState: '_loading',
        noResults: '_no-results',
        noChoices: '_no-choices',
      },
      // https://github.com/krisk/Fuse#options
      fuseOptions: {
        include: 'score',
      },
    });

    // Fix placeholder for select-one
    if (instance.isSelectOneElement && selectOnePlaceholder) {
      fixSelectOnePlaceholder();
      if (selectOneInitialValue === '' && (!config.items || !config.items.length)) {
        instance.setValueByChoice(selectOneInitialValue);
      }
    }

    // Disable if elemen disabled
    if (isDisabled) {
      instance.disable();
    }

    // Setup ajax
    if (ajax) {
      if (ajax.initial) {
        fetchChoices();
      }

      if (ajax.search) {
        instance.passedElement.addEventListener('search', (e) => {
          const query = e.detail.value;

          if (ajaxTimer !== null) {
            clearTimeout(ajaxTimer);
          }

          ajaxTimer = setTimeout(() => {
            fetchChoices(query);
          }, ajax.delay);
        });

        // If search cleared
        // https://github.com/jshjohnson/Choices/issues/74
        if (instance.input) {
          instance.input.addEventListener('input', () => {
            if (!instance.input.value) {
              fetchChoices();
            }
          });
        }
      }
    }

    this.instances.set(el, instance);
  }

  destroyInstance(instance) {
    instance.destroy();
  }
}

export default new ChoicesModule();
